/**
 * YourBuildWidget Component
 *
 * Displays the user's current build/configuration summary.
 * Non-reusable widget - only one per page.
 * Matches the existing UI-Model styling.
 */

import React, { useState } from 'react';
import { YourBuildWidgetProps } from './types';
import { YOUR_BUILD_DEFAULT_CONFIG, mergeYourBuildWithDefaults } from '../../config/widgetDefaults';
import { BuildIcon } from 'assests/GmsIcons';

export const YourBuildWidget = ({
  config,
  isSelected,
  onSelect,
}: YourBuildWidgetProps) => {
  // Merge config with defaults to ensure all fields exist
  const mergedConfig = mergeYourBuildWithDefaults(config || YOUR_BUILD_DEFAULT_CONFIG);

  const {
    showTitle,
    title,
    showShareButton,
    shareButtonConfig,
    showDescription,
    description,
    showAddToCartButton,
    addToCartButtonConfig,
    descriptionTitle,
    showDescriptionTitle,
    descriptionTitleCollapseExpand,
  } = mergedConfig;
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(true);
  const backgroundClass = isSelected ? 'tw-bg-transparent' : 'tw-bg-surface-sunken';

  return (
    <div role="button" data-testid="widgets-yourbuildwidget-div-1"
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      className={`tw-w-full tw-h-full tw-flex tw-flex-col tw-p-4 tw-cursor-pointer tw-transition-all ${backgroundClass} ${
        isSelected ? 'tw-ring-2 tw-ring-accent tw-ring-offset-2' : ''
      }`}
    >
      {/* Header Section */}
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        {showTitle && (
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-text-ink icon-fill-current"><BuildIcon /></span>
            <h2 className="tw-text-sm tw-font-semibold tw-text-ink">
              {title}
            </h2>
          </div>
        )}

        {showShareButton && (
          <button data-testid="widgets-yourbuildwidget-button-1"
            type="button"
            disabled
            className="tw-px-4 tw-py-1 tw-border tw-border-edge-strong tw-bg-surface-sunken tw-text-ink-disabled tw-text-sm tw-flex tw-items-center tw-gap-2"
          >
            {shareButtonConfig.showIcon && <ShareIcon className="tw-w-4 tw-h-4" />}
            {shareButtonConfig.showText && shareButtonConfig.buttonLabel}
          </button>
        )}
      </div>

      {/* Build Content Placeholder */}
      <div className="tw-flex-1 tw-min-h-[60px]">
        {/* Build summary will appear here at runtime */}
      </div>

      {/* Description Title Section */}
      {showDescriptionTitle && descriptionTitle && (
        <div role="button" data-testid="widgets-yourbuildwidget-div-2"
          id="your-build-description-title-row"
          className={`tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 ${
            descriptionTitleCollapseExpand ? 'tw-cursor-pointer' : ''
          }`}
          onClick={
            descriptionTitleCollapseExpand
              ? (e) => {
                  e.stopPropagation();
                  setIsDescriptionExpanded((prev) => !prev);
                }
              : undefined
          }
        >
          <span id="your-build-description-title-text" className="tw-text-ink tw-text-[13px] tw-font-semibold">
            {descriptionTitle}
          </span>
          {descriptionTitleCollapseExpand && (
            <ChevronIcon
              id="your-build-description-title-chevron"
              className={`tw-w-4 tw-h-4 tw-text-ink tw-transition-transform tw-duration-200 ${
                isDescriptionExpanded ? 'tw-rotate-180' : ''
              }`}
            />
          )}
        </div>
      )}

      {/* Description Section */}
      {showDescription &&
        description &&
        (!descriptionTitleCollapseExpand || isDescriptionExpanded) && (
          <div className="tw-mb-4 tw-px-3 tw-overflow-hidden">
            <div
              className="tw-text-ink tw-text-[11px] tw-break-words tw-max-w-full"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </div>
        )}

      {/* Add to Cart Button */}
      {showAddToCartButton && (
        <div className="tw-flex tw-justify-center">
          <button data-testid="widgets-yourbuildwidget-button-2"
            type="button"
            disabled
            className="tw-px-6 tw-py-2 tw-border tw-border-edge-strong tw-bg-surface-sunken tw-text-ink-disabled tw-text-sm tw-flex tw-items-center tw-gap-2"
          >
            {addToCartButtonConfig.showIcon && <CartIcon className="tw-w-4 tw-h-4" />}
            {addToCartButtonConfig.showText && addToCartButtonConfig.buttonLabel}
          </button>
        </div>
      )}
    </div>
  );
};

export default YourBuildWidget;

// Icon components (matching existing UI-Model)
const ShareIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m9.032 4.026a3 3 0 10-4.026 4.026 3 3 0 004.026-4.026zM21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const ChevronIcon = ({ className, id }: { className?: string; id?: string }) => (
  <svg id={id} className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const CartIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
    />
  </svg>
);
